// Helper functions for room operations

/**
 * Format room data for submission to the API
 * @param {Object} formData - The form data from the Travelomatix room form
 * @returns {Object} - Formatted data ready for API submission
 */
export const formatRoomDataForSubmission = (formData) => {
  // Filter out empty image URLs
  const validImageUrls = formData.images_urls.filter(url => url.trim() !== '');
  
  // Prepare data for submission
  const submitData = {
    hotel_id: formData.hotel_id,
    name_en: formData.name_en,
    supplier_id: formData.supplier_id,
    room_type: formData.room_type,
    capacity: formData.capacity,
    // Add accessibility template IDs
    accessibility_template_ids: formData.accessibility_template_ids || [],
    images_urls: validImageUrls.length > 0 ? validImageUrls : undefined
  };
  
  // Remove undefined fields
  Object.keys(submitData).forEach(key => {
    if (submitData[key] === undefined) {
      delete submitData[key];
    }
  });
  
  return submitData;
};

/**
 * Validate room form data
 * @param {Object} formData - The form data to validate
 * @returns {Object} - Validation errors (empty if valid)
 */
export const validateRoomForm = (formData) => {
  const errors = {};
  
  if (!formData.hotel_id) {
    errors.hotel_id = 'Hotel is required';
  }
  
  if (!formData.room_type.trim()) {
    errors.room_type = 'Room type is required';
  }
  
  if (!formData.price_per_night || formData.price_per_night <= 0) {
    errors.price_per_night = 'Price per night must be greater than 0';
  }
  
  if (formData.capacity < 1 || formData.capacity > 6) {
    errors.capacity = 'Capacity must be between 1 and 6';
  }
  
  return errors;
};

/**
 * Validate Travelomatix room form data
 * @param {Object} formData - The form data to validate
 * @returns {Object} - Validation errors (empty if valid)
 */
export const validateTravelomatixRoomForm = (formData) => {
  const errors = {};
  
  if (!formData.hotel_id) {
    errors.hotel_id = 'Hotel is required';
  }
  
  if (!formData.name_en.trim()) {
    errors.name_en = 'Room English name is required';
  }
  
  // Validate image URLs
  const validImageUrls = formData.images_urls.filter(url => url.trim() !== '');
  if (validImageUrls.some(url => !/^https?:\/\/.+\..+/.test(url))) {
    errors.images_urls = 'All image URLs must be valid URLs';
  }
  
  return errors;
};

/**
 * Initialize room form data for adding a new room
 * @param {Object} selectedHotel - The selected hotel object
 * @param {Object} roomData - The room data from Travelomatix
 * @returns {Object} - Initialized form data
 */
export const initializeRoomFormDataForAdd = (selectedHotel, roomData) => {
  return {
    hotel_id: selectedHotel?.id || '',
    name_en: roomData.RoomTypeName || '',
    name_ar: '',
    supplier_id: roomData.HOTEL_CODE || '',
    room_type: roomData.RoomTypeName || '',
    capacity: roomData.RoomGuests ? roomData.RoomGuests.NoOfAdults : 2,
    price_per_night: '',
    currency: 'USD',
    images_urls: [''],
    // Add accessibility template IDs
    accessibility_template_ids: []
  };
};

/**
 * Initialize room form data for editing an existing room
 * @param {Object} room - The existing room object
 * @returns {Object} - Initialized form data
 */
export const initializeRoomFormDataForEdit = (room) => {
  // Extract accessibility template IDs from the nested accessibility_template structure
  let accessibilityTemplateIds = [];
  
  if (room.accessibility_template && typeof room.accessibility_template === 'object') {
    // Iterate through each category in accessibility_template
    Object.values(room.accessibility_template).forEach(category => {
      if (category && typeof category === 'object') {
        // Process feature_categories
        if (Array.isArray(category.feature_categories)) {
          category.feature_categories.forEach(featureCategory => {
            if (featureCategory && featureCategory.id) {
              accessibilityTemplateIds.push(featureCategory.id);
            }
            // Process features within feature categories
            if (Array.isArray(featureCategory.features)) {
              featureCategory.features.forEach(feature => {
                if (feature && feature.id) {
                  accessibilityTemplateIds.push(feature.id);
                }
              });
            }
          });
        }
        
        // Process features directly under category
        if (Array.isArray(category.features)) {
          category.features.forEach(feature => {
            if (feature && feature.id) {
              accessibilityTemplateIds.push(feature.id);
            }
          });
        }
      }
    });
  }
  
  return {
    hotel_id: room.hotel_id || '',
    name_en: room.name_en || '',
    name_ar: room.name_ar || '',
    supplier_id: room.supplier_id || '',
    room_type: room.room_type || '',
    capacity: room.capacity || 1,
    price_per_night: room.price_per_night || '',
    currency: room.currency || 'USD',
    images_urls: room.images_urls && Array.isArray(room.images_urls) ? room.images_urls : [''],
    // Add extracted accessibility template IDs
    accessibility_template_ids: accessibilityTemplateIds
  };
};

export default {
  formatRoomDataForSubmission,
  validateTravelomatixRoomForm,
  initializeRoomFormDataForAdd,
  initializeRoomFormDataForEdit
};